import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const LogoManagementTab = ({ config, onUpdate }) => {
  const [selectedVariant, setSelectedVariant] = useState('primary');
  const [logoFiles, setLogoFiles] = useState({
    primary: null,
    white: null,
    dark: null,
    icon: null
  });

  const logoVariants = [
    {
      id: 'primary',
      name: 'Primary Logo',
      description: 'Main logo with CLAB branding',
      context: 'Light backgrounds, headers, documents',
      preview: config?.logoUrl || '/assets/images/image-1750685807825.png'
    },
    {
      id: 'white',
      name: 'White Logo',
      description: 'Logo variant for dark backgrounds',
      context: 'Dark backgrounds, headers, overlays',
      preview: config?.logoUrl || '/assets/images/image-1750685807825.png'
    },
    {
      id: 'dark',
      name: 'Dark Logo',
      description: 'Dark variant for light backgrounds',
      context: 'Light backgrounds, documents, print',
      preview: config?.logoUrl || '/assets/images/image-1750685807825.png'
    },
    {
      id: 'icon',
      name: 'Icon Only',
      description: 'Hard hat icon without text',
      context: 'Compact spaces, favicons, mobile',
      preview: config?.logoUrl || '/assets/images/image-1750685807825.png'
    }
  ];

  const logoSizes = [
    { id: 'xs', name: 'Extra Small', size: '16px', usage: 'Favicons, small icons' },
    { id: 'sm', name: 'Small', size: '24px', usage: 'Mobile navigation, breadcrumbs' },
    { id: 'md', name: 'Medium', size: '32px', usage: 'Header navigation, forms' },
    { id: 'lg', name: 'Large', size: '48px', usage: 'Page headers, welcome screens' },
    { id: 'xl', name: 'Extra Large', size: '64px', usage: 'Login screens, hero sections' },
    { id: '2xl', name: '2X Large', size: '96px', usage: 'Splash screens, presentations' }
  ];

  const usageContexts = [
    {
      id: 'header',
      name: 'Header Navigation',
      description: 'Logo placement in main navigation',
      example: (
        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src={config?.logoUrl || '/assets/images/image-1750685807825.png'} 
                alt="CLAB Logo" 
                className="h-8 w-auto"
              />
              <span className="font-semibold text-text-primary">CLAB Platform</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-text-secondary">Dashboard</span>
              <Icon name="User" size={24} className="text-text-secondary" />
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'login',
      name: 'Login Screen',
      description: 'Logo on authentication pages',
      example: (
        <div className="bg-gradient-to-r from-[#2D9B9B]/10 to-[#1A7A7A]/10 border border-secondary-200 rounded-lg p-8 text-center">
          <img 
            src={config?.logoUrl || '/assets/images/image-1750685807825.png'} 
            alt="CLAB Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h2 className="text-xl font-semibold text-text-primary mb-2">Welcome to CLAB</h2>
          <p className="text-text-secondary text-sm">Construction & Engineering Biodata Management</p>
        </div>
      )
    },
    {
      id: 'document',
      name: 'Document Header',
      description: 'Logo in printed documents and PDFs',
      example: (
        <div className="bg-white border border-secondary-200 rounded-lg p-6">
          <div className="flex items-center justify-between border-b border-secondary-200 pb-4">
            <div className="flex items-center space-x-3">
              <img 
                src={config?.logoUrl || '/assets/images/image-1750685807825.png'} 
                alt="CLAB Logo" 
                className="h-6 w-auto"
              />
              <div>
                <h3 className="font-medium text-text-primary">CLAB</h3>
                <p className="text-xs text-text-secondary">Biodata Report</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-text-primary">Report Date: March 2024</p>
              <p className="text-xs text-text-secondary">Document ID: CLB-2024-001</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleLogoUpload = (variant, event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoFiles(prev => ({
          ...prev,
          [variant]: file
        }));
        onUpdate({ [`${variant}LogoUrl`]: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const downloadLogo = (variant) => {
    // In a real implementation, this would download the actual logo file
  };

  return (
    <div className="space-y-8">
      {/* Logo Variants */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Image" size={20} className="mr-2 text-[#2D9B9B]" />
          Logo Variants
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {logoVariants.map((variant) => (
            <div key={variant.id} className="bg-surface border border-secondary-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-text-primary">{variant.name}</h4>
                  <p className="text-sm text-text-secondary">{variant.description}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadLogo(variant.id)}
                    className="p-2 text-text-secondary hover:text-text-primary rounded-lg hover:bg-secondary-100 transition-colors"
                    title="Download"
                  >
                    <Icon name="Download" size={16} />
                  </button>
                  <label className="p-2 text-text-secondary hover:text-text-primary rounded-lg hover:bg-secondary-100 transition-colors cursor-pointer">
                    <Icon name="Upload" size={16} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(variant.id, e)}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Logo Preview */}
              <div className={`aspect-video rounded-lg border-2 border-dashed border-secondary-300 flex items-center justify-center mb-4 ${
                variant.id === 'white' ? 'bg-secondary-800' : 'bg-secondary-50'
              }`}>
                {variant.preview ? (
                  <img 
                    src={variant.preview} 
                    alt={variant.name}
                    className="max-h-16 w-auto"
                  />
                ) : (
                  <div className="text-center">
                    <Icon name="Image" size={32} className="text-secondary-400 mx-auto mb-2" />
                    <p className="text-sm text-secondary-500">No logo uploaded</p>
                  </div>
                )}
              </div>

              <div className="text-xs text-text-muted">
                <strong>Best for:</strong> {variant.context}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Logo Sizes */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Maximize" size={20} className="mr-2 text-[#2D9B9B]" />
          Logo Sizes
        </h3>
        <div className="bg-surface border border-secondary-200 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {logoSizes.map((size) => (
              <div key={size.id} className="text-center">
                <div className="bg-secondary-50 rounded-lg p-4 mb-3 flex items-center justify-center" style={{ minHeight: '80px' }}>
                  <img 
                    src={config?.logoUrl || '/assets/images/image-1750685807825.png'} 
                    alt="CLAB Logo"
                    style={{ height: size.size, width: 'auto' }}
                  />
                </div>
                <div>
                  <h4 className="font-medium text-text-primary text-sm">{size.name}</h4>
                  <p className="text-xs text-text-secondary">{size.size}</p>
                  <p className="text-xs text-text-muted mt-1">{size.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Usage Contexts */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Layout" size={20} className="mr-2 text-[#2D9B9B]" />
          Usage Contexts
        </h3>
        <div className="space-y-6">
          {usageContexts.map((context) => (
            <div key={context.id} className="bg-surface border border-secondary-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-text-primary">{context.name}</h4>
                  <p className="text-sm text-text-secondary">{context.description}</p>
                </div>
              </div>
              <div className="bg-secondary-50 rounded-lg p-4">
                {context.example}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Logo Guidelines */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Shield" size={20} className="mr-2 text-[#2D9B9B]" />
          Logo Guidelines
        </h3>
        <div className="bg-gradient-to-r from-[#2D9B9B]/5 to-[#1A7A7A]/5 border border-[#2D9B9B]/20 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Do's */}
            <div>
              <h4 className="font-medium text-text-primary mb-4 flex items-center">
                <Icon name="Check" size={16} className="mr-2 text-success" />
                Do's
              </h4>
              <ul className="space-y-3 text-sm text-text-secondary">
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Maintain minimum clear space around the logo</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Use high contrast between logo and background</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Preserve the original aspect ratio when scaling</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Use appropriate variant for the context</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Ensure logo is readable at minimum size</span>
                </li>
              </ul>
            </div>

            {/* Don'ts */}
            <div>
              <h4 className="font-medium text-text-primary mb-4 flex items-center">
                <Icon name="X" size={16} className="mr-2 text-error" />
                Don'ts
              </h4>
              <ul className="space-y-3 text-sm text-text-secondary">
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={14} className="text-error mt-0.5 flex-shrink-0" />
                  <span>Stretch or distort the logo proportions</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={14} className="text-error mt-0.5 flex-shrink-0" />
                  <span>Change the logo colors or apply filters</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={14} className="text-error mt-0.5 flex-shrink-0" />
                  <span>Place logo on busy or low-contrast backgrounds</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={14} className="text-error mt-0.5 flex-shrink-0" />
                  <span>Use logo smaller than the minimum size</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="X" size={14} className="text-error mt-0.5 flex-shrink-0" />
                  <span>Rotate or create effects on the logo</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Clear Space Guidelines */}
          <div className="mt-8 pt-6 border-t border-[#2D9B9B]/20">
            <h4 className="font-medium text-text-primary mb-4">Clear Space Guidelines</h4>
            <div className="bg-white rounded-lg p-6 border border-secondary-200">
              <div className="flex items-center justify-center">
                <div className="relative">
                  <img 
                    src={config?.logoUrl || '/assets/images/image-1750685807825.png'} 
                    alt="CLAB Logo"
                    className="h-16 w-auto"
                  />
                  <div className="absolute inset-0 border-2 border-dashed border-[#2D9B9B] opacity-50 -m-4"></div>
                </div>
              </div>
              <p className="text-sm text-text-secondary text-center mt-4">
                Minimum clear space equals the height of the hard hat icon
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Download" size={20} className="mr-2 text-[#2D9B9B]" />
          Export Options
        </h3>
        <div className="bg-surface border border-secondary-200 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {['PNG', 'SVG', 'PDF', 'AI'].map((format) => (
              <button
                key={format}
                className="flex items-center justify-center space-x-2 p-4 border border-secondary-300 rounded-lg hover:border-[#2D9B9B] hover:bg-[#2D9B9B]/5 transition-colors"
              >
                <Icon name="Download" size={16} />
                <span className="font-medium">{format}</span>
              </button>
            ))}
          </div>
          <div className="mt-4 text-sm text-text-secondary">
            <p>Available formats: PNG (web use), SVG (scalable), PDF (print), AI (Adobe Illustrator)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoManagementTab;